import { type CSSProperties, type FormEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { keyframes, styled } from "@pigment-css/react";
import { VisuallyHidden } from "radix-ui";
import { MicrophoneIcon, Stop } from "@acto/web-common/components";
import { useTranslations } from "@acto/web-common/locales";
import type { GlobalTokens } from "@acto/web-common/theme";
import { glassCapsulePress } from "@acto/taskpane/field-sales-2/styles/glassCapsulePress";
import { useChatDictation } from "./useChatDictation";
import { VoiceIndicator } from "./VoiceIndicator";
import {
  BUTTON_COLOR_MS,
  EASE,
  FIELD_COLLAPSE_MS,
  FIELD_GROW_MS,
  FIELD_OPEN_MS,
  ICON_CROSSFADE_DELAY_MS,
  ICON_CROSSFADE_MS,
  LINE_FADE_MS,
  prefersReducedMotion,
  PRESS_TRANSITION,
  REDUCED_MOTION,
} from "./motion";

const MIN_LINES = 3;
const MAX_LINES = 5;

export const IDLE_HEIGHT_PX = 56;
const LINE_HEIGHT_PX = 24;
// One value serves both states: CSS clamps border-radius to half the box, so a radius that differs
// per state stays clamped while the field is short and cannot animate in step with the height.
const FIELD_RADIUS = `${IDLE_HEIGHT_PX / 2}px`;
const PAD_TOP_PX = 16;
const BUTTON_PX = 40;
const BUTTON_INSET_BOTTOM_PX = 8;
// The card reserves the button's box, so the button sits at the same inset in both states.
const CARD_BOTTOM_SPACE_PX = 10 + BUTTON_PX + BUTTON_INSET_BOTTOM_PX;
const IDLE_TEXT_INSET_PX = 40;
const OVERFLOW_FADE_PX = LINE_HEIGHT_PX;

const lineFadeIn = keyframes({
  from: { opacity: 0 },
  to: { opacity: 1 },
});

const composerSurface = (theme: GlobalTokens) =>
  ({
    boxSizing: "border-box",
    width: "100%",
    border: `1px solid ${theme.fieldSalesTwo.colors.surface.glassBorder}`,
    backdropFilter: "blur(3px)",
    WebkitBackdropFilter: "blur(3px)",
    boxShadow: theme.fieldSalesTwo.shadows.glass,
    cursor: "text",
  }) as const;

/** The mirror lays out over the textarea glyph for glyph; any difference here puts the dimmed tail in the wrong place. */
const composerText = (theme: GlobalTokens) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  // Below 16px, iOS/WKWebView zooms the page on focus.
  fontSize: theme.fieldSalesTwo.font.size.input,
  fontWeight: theme.fieldSalesTwo.font.weight[520],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.subtle,
  color: theme.fieldSalesTwo.colors.neutral[900],
});

const Form = styled("form")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[12],
  width: "100%",
}));

/**
 * The one element the whole composer morphs within. Idle and active are the same box at different
 * heights, never two elements swapped, because opening animates between them and a swap cannot be
 * transitioned.
 */
const Field = styled("div")<{ expanded?: boolean }>(({ theme }) => ({
  ...composerSurface(theme),
  position: "relative",
  overflow: "hidden",
  paddingTop: theme.fieldSalesTwo.spacing[16],
  paddingInline: theme.fieldSalesTwo.spacing[20],
  borderRadius: FIELD_RADIUS,
  background: theme.fieldSalesTwo.colors.surface.glass,
  transitionProperty: "height, background-color",
  transitionTimingFunction: EASE,
  // A custom property, not an inline `transition-duration`: an inline style would outrank the
  // reduced-motion block below.
  transitionDuration: "var(--field-duration)",
  [REDUCED_MOTION]: {
    transitionDuration: "0s",
  },
  variants: [
    {
      props: { expanded: true },
      style: {
        background: theme.fieldSalesTwo.colors.surface.glassMedium,
        backdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
        WebkitBackdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
      },
    },
  ],
}));

const TextLayer = styled("div")({
  position: "relative",
  width: "100%",
});

const Textarea = styled("textarea")<{ textHidden?: boolean; expanded?: boolean }>(({ theme }) => ({
  display: "block",
  boxSizing: "border-box",
  width: "100%",
  margin: 0,
  padding: 0,
  paddingRight: `${IDLE_TEXT_INSET_PX}px`,
  border: "none",
  outline: "none",
  resize: "none",
  background: "transparent",
  overflowY: "hidden",
  ...composerText(theme),
  caretColor: theme.fieldSalesTwo.colors.charts.primary,
  transitionProperty: "padding-right",
  transitionTimingFunction: EASE,
  [REDUCED_MOTION]: {
    transition: "none",
  },
  "&::placeholder": {
    color: theme.fieldSalesTwo.colors.neutral[700],
  },
  variants: [
    {
      // Once the button drops below the text there is nothing to clear, so the text runs full width.
      props: { expanded: true },
      style: { paddingRight: 0, overflowY: "auto" },
    },
    {
      props: { textHidden: true },
      style: { color: "transparent" },
    },
  ],
}));

/**
 * A non-interactive copy of the textarea's content, laid out identically underneath it. Exists only
 * so the provisional tail can be dimmed — a textarea cannot style a substring of its own value.
 */
const Mirror = styled("div")(({ theme }) => ({
  position: "absolute",
  inset: 0,
  overflow: "hidden",
  pointerEvents: "none",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  ...composerText(theme),
}));

const Provisional = styled("span")(({ theme }) => ({
  color: theme.fieldSalesTwo.colors.neutral[700],
  animation: `${lineFadeIn} ${LINE_FADE_MS}ms ease`,
  [REDUCED_MOTION]: {
    animation: "none",
  },
}));

/** Text, not a link: no web API can open the platform's microphone settings. That needs a native host. */
const PermissionCaption = styled("p")(({ theme }) => ({
  margin: 0,
  width: "100%",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[14],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[168],
  color: theme.fieldSalesTwo.colors.neutral[700],
  textAlign: "center",
}));

const ControlRow = styled("div")(({ theme }) => ({
  position: "absolute",
  left: theme.fieldSalesTwo.spacing[20],
  right: theme.fieldSalesTwo.spacing[10],
  bottom: `${BUTTON_INSET_BOTTOM_PX}px`,
  height: `${BUTTON_PX}px`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  pointerEvents: "none",
  "& > *": {
    pointerEvents: "auto",
  },
}));

const CircleButton = styled("button")<{ tone?: "send" | "stop" | "mic" }>(({ theme }) => ({
  position: "relative",
  flex: "0 0 auto",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: theme.fieldSalesTwo.spacing[44], // No 40px spacing token; 44 is the nearest.
  height: theme.fieldSalesTwo.spacing[44],
  padding: 0,
  border: "none",
  borderRadius: theme.fieldSalesTwo.radius.circular,
  backdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  WebkitBackdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  boxShadow: theme.fieldSalesTwo.shadows.glass,
  cursor: "pointer",
  color: theme.fieldSalesTwo.colors.neutral[900],
  background: theme.fieldSalesTwo.colors.neutral[300],
  ...glassCapsulePress(theme, { restShadow: theme.fieldSalesTwo.shadows.glass }),
  transition: `${PRESS_TRANSITION}, background-color ${BUTTON_COLOR_MS}ms ease, color ${BUTTON_COLOR_MS}ms ease`,
  // Repeats glassCapsulePress's transform overrides: declaring this key after the spread replaces
  // its block outright, so dropping them would leave the button lifting under reduced motion.
  [REDUCED_MOTION]: {
    transition: PRESS_TRANSITION,
    "&:hover": { transform: "none" },
    "&:active": { transform: "none" },
  },
  variants: [
    {
      props: { tone: "send" },
      style: {
        background: theme.fieldSalesTwo.colors.charts.primary,
        color: "#ffffff",
      },
    },
    {
      props: { tone: "stop" },
      style: {
        background: theme.fieldSalesTwo.colors.neutral[900],
        color: "#ffffff",
      },
    },
    {
      props: { tone: "mic" },
      style: {
        background: theme.fieldSalesTwo.colors.charts.primary,
        color: "#ffffff",
      },
    },
  ],
  "&:disabled": {
    cursor: "not-allowed",
  },
}));

/**
 * The icons stay mounted and cross-fade; swapping them would reflow the button mid-transition.
 */
const IconLayer = styled("span")<{ shown?: boolean }>({
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  opacity: 0,
  transition: `opacity ${ICON_CROSSFADE_MS}ms ease ${ICON_CROSSFADE_DELAY_MS}ms`,
  [REDUCED_MOTION]: {
    transition: "none",
  },
  variants: [{ props: { shown: true }, style: { opacity: 1 } }],
});

function ArrowUp() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M10 15.5V4.5M10 4.5L5.25 9.25M10 4.5l4.75 4.75"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

type ButtonMode = "mic" | "stop" | "send";

interface ChatInputProps {
  className?: string;
  value?: string;
  placeholder: string;
  disabled?: boolean;
  loading?: boolean;
  onSend: () => void;
  onAbort?: () => void;
  onChange?: (value: string) => void;
}

export function ChatInput({
  value: controlledValue,
  placeholder,
  disabled = false,
  loading = false,
  onSend,
  onAbort,
  onChange,
  className,
}: ChatInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mirrorRef = useRef<HTMLDivElement>(null);
  const loudnessRef = useRef<HTMLDivElement>(null);
  const wasExpandedRef = useRef(false);
  // Measured once: reading it forces a style recalc, and a unitless line height off a fixed root
  // font size cannot change while the component is mounted.
  const lineHeightRef = useRef(0);
  const [metrics, setMetrics] = useState(0);
  const [internalValue, setInternalValue] = useState("");
  const [field, setField] = useState({ height: IDLE_HEIGHT_PX, duration: FIELD_OPEN_MS });
  const [fade, setFade] = useState({ top: false, bottom: false });
  const tChat = useTranslations("chat");

  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : internalValue;

  const commitValue = (newValue: string) => {
    if (!isControlled) setInternalValue(newValue);
    onChange?.(newValue);
  };

  const dictation = useChatDictation({ value, onChange: commitValue, loudnessTarget: loudnessRef });

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    // Typing is the way out of every dictation state.
    dictation.interrupt();
    commitValue(e.target.value);
  };

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed || disabled || loading) return;

    dictation.interrupt();
    // onSend() before clearing: the cleared value is what starts the collapse.
    onSend();
    if (!isControlled) setInternalValue("");
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    submit();
  };

  const startDictation = () => {
    // iOS only opens the keyboard for a focus() issued inside the tap itself.
    textareaRef.current?.focus();
    dictation.start();
  };

  const hasInput = value.trim().length > 0;
  // The composer stays editable while a response streams, so `loading` guards submission here
  // instead of on the input. Sending mid-run would interleave a second turn into the thread.
  const canSend = hasInput && !disabled && !loading;

  const expanded = dictation.dictating || hasInput || dictation.notice !== "none";

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Width and font decide how many lines the text wraps into, and neither change re-renders this
    // component on its own.
    const remeasure = () => setMetrics((n) => n + 1);

    // Text measured in a fallback font wraps into a different number of lines than the real one.
    void document.fonts?.ready.then(remeasure);

    // Width only: the measurement writes the height back, so reacting to height would loop. The
    // observer's first delivery carries the width the mount measurement already used.
    let lastWidth: number | null = null;
    const observer = new ResizeObserver(([entry]) => {
      const width = entry.borderBoxSize[0].inlineSize;
      const previousWidth = lastWidth;
      lastWidth = width;

      if (previousWidth !== null && width !== previousWidth) remeasure();
    });

    observer.observe(textarea, { box: "border-box" });
    return () => observer.disconnect();
  }, []);

  // A static mask would dim the outermost line when nothing is scrolled past that edge, so the
  // fade is driven by scroll position.
  const updateFade = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const top = textarea.scrollTop > 1;
    const bottom = textarea.scrollTop + textarea.clientHeight < textarea.scrollHeight - 1;
    setFade((prev) => (prev.top === top && prev.bottom === bottom ? prev : { top, bottom }));
  }, []);

  // Height is measured and applied rather than left to `auto`, because `auto` cannot be
  // transitioned and opening, growing and collapsing are all height animations on this one box.
  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    if (!lineHeightRef.current) {
      // getComputedStyle can report `normal`, which parseFloat turns to NaN and collapses the field.
      lineHeightRef.current = parseFloat(window.getComputedStyle(textarea).lineHeight) || LINE_HEIGHT_PX;
    }
    const lineHeight = lineHeightRef.current;
    const minLines = expanded ? MIN_LINES : 1;
    const maxLines = expanded ? MAX_LINES : 1;

    textarea.style.height = "auto";
    const textHeight = Math.min(Math.max(textarea.scrollHeight, minLines * lineHeight), maxLines * lineHeight);
    textarea.style.height = `${textHeight}px`;

    const crossing = expanded !== wasExpandedRef.current;
    wasExpandedRef.current = expanded;

    const height = expanded ? PAD_TOP_PX + textHeight + CARD_BOTTOM_SPACE_PX : IDLE_HEIGHT_PX;
    const duration = crossing ? (expanded ? FIELD_OPEN_MS : FIELD_COLLAPSE_MS) : FIELD_GROW_MS;
    // Returning the same object is what keeps a keystroke that changes no line from re-rendering.
    setField((prev) => (prev.height === height && prev.duration === duration ? prev : { height, duration }));

    if (dictation.recording) {
      textarea.scrollTo({ top: textarea.scrollHeight, behavior: prefersReducedMotion() ? "auto" : "smooth" });
    }
    if (mirrorRef.current) mirrorRef.current.scrollTop = textarea.scrollTop;
    updateFade();
  }, [value, expanded, dictation.recording, metrics, updateFade]);

  const provisionalStart = Math.max(value.length - dictation.provisionalLength, 0);
  const fadeMask =
    fade.top || fade.bottom
      ? `linear-gradient(to bottom, ${fade.top ? "transparent" : "black"} 0, black ${OVERFLOW_FADE_PX}px, black calc(100% - ${OVERFLOW_FADE_PX}px), ${fade.bottom ? "transparent" : "black"} 100%)`
      : undefined;
  const noticeText = dictation.notice === "nothing-heard" ? tChat("dictationNothingHeard") : null;
  const placeholderText = noticeText ?? placeholder;
  const dictationAnnouncement = dictation.recording ? tChat("dictationRecording") : (noticeText ?? "");

  const mode: ButtonMode =
    dictation.dictating || (loading && Boolean(onAbort)) ? "stop" : hasInput || dictation.micBlocked ? "send" : "mic";

  const buttonProps =
    mode === "stop"
      ? {
          onClick: dictation.dictating ? dictation.stop : onAbort,
          "aria-label": dictation.dictating ? tChat("stopDictation") : tChat("stopGenerating"),
        }
      : mode === "mic"
        ? { onClick: startDictation, "aria-label": tChat("startDictation") }
        : { onClick: submit, disabled: !canSend, "aria-label": tChat("sendMessage") };

  // Stays `type="button"` in all three modes. A mode-dependent `type="submit"` would be evaluated
  // after this click's handlers, by which point stopping the dictation has already turned the
  // control into send, so the stop tap would also submit.
  const actionButton = (
    <CircleButton type="button" tone={mode === "send" && !canSend ? undefined : mode} {...buttonProps}>
      <IconLayer shown={mode === "mic"}>
        <MicrophoneIcon />
      </IconLayer>
      <IconLayer shown={mode === "stop"}>
        <Stop />
      </IconLayer>
      <IconLayer shown={mode === "send"}>
        <ArrowUp />
      </IconLayer>
    </CircleButton>
  );

  return (
    <Form onSubmit={handleSubmit} className={className}>
      <Field
        expanded={expanded}
        onClick={() => textareaRef.current?.focus()}
        style={
          {
            height: `${field.height}px`,
            "--field-duration": `${field.duration}ms`,
          } as CSSProperties
        }
      >
        <TextLayer style={fadeMask ? { maskImage: fadeMask, WebkitMaskImage: fadeMask } : undefined}>
          {dictation.recording && (
            <Mirror ref={mirrorRef} aria-hidden="true">
              {value.slice(0, provisionalStart)}
              <Provisional key={provisionalStart}>{value.slice(provisionalStart)}</Provisional>
            </Mirror>
          )}
          <Textarea
            ref={textareaRef}
            rows={1}
            name="message"
            autoComplete="off"
            value={value}
            expanded={expanded}
            textHidden={dictation.recording}
            onChange={handleChange}
            onScroll={() => {
              if (mirrorRef.current && textareaRef.current) {
                mirrorRef.current.scrollTop = textareaRef.current.scrollTop;
              }
              updateFade();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            placeholder={placeholderText}
            aria-label={tChat("composerLabel")}
          />
        </TextLayer>
        <ControlRow>
          <VoiceIndicator ref={loudnessRef} visible={dictation.recording} settling={dictation.settling} />
          {actionButton}
        </ControlRow>
      </Field>
      {/*
       * Stays mounted and empty: a text change inside a live region is announced, the arrival of
       * a new region is not.
       */}
      <VisuallyHidden.Root role="status">{dictationAnnouncement}</VisuallyHidden.Root>
      {dictation.micBlocked && <PermissionCaption role="status">{tChat("dictationBlocked")}</PermissionCaption>}
    </Form>
  );
}
